import { useCallback, useEffect, useState, type FC } from "react";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { HistoryIcon, MapPinnedIcon, Search } from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import Text from "../Text";
import { updateTextOfSearchBar } from "@/store/searchBarState";
import useDebounce from "@/hooks/useDebounce";
import {
  autoCompleteSearchBarState,
  resetAutoCompleteSearchBarState,
  searchForAutoCompleteSearchBar,
} from "@/store/autoCompleteSearchBarState";
import { AutocompleteResultType } from "@/API";
import { useNavigate } from "react-router";
import { searchHistoryState } from "@/store/searchHistoryState";
import useGooglePlacesAutoComplete from "@/hooks/useGooglePlacesAutoComplete";
import { setGooglePlace } from "@/store/getGooglePlaceState";
import { useTranslation } from "react-i18next";
import { updateTextOfSearchTextBar } from "@/store/searchBarTextState";
import { triggerEvent } from "@/lib/utils";
import { FAMOUS_PLACE } from "@/lib/constants";

const IDS = ["search-bar-input", "search-bar-popover"];

const SearchBar: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [lastResearchState, setLastResearchState] = useState<string[]>([]);

  const debouncedValue = useDebounce<string>(value);
  const {
    suggestions,
    fetchAutocompleteSuggestions,
    fetchFieldsDetails,
    googlePlace,
  } = useGooglePlacesAutoComplete();

  const { data } = autoCompleteSearchBarState();
  const { searchHistoryItem } = searchHistoryState();

  useEffect(() => {
    if (googlePlace.location && googlePlace.boundingBox) {
      setGooglePlace(googlePlace);
    }
  }, [googlePlace]);

  useEffect(() => {
    if (debouncedValue.length > 2) {
      fetchAutocompleteSuggestions(debouncedValue);
      searchForAutoCompleteSearchBar({ search: debouncedValue });
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (searchHistoryItem) {
      const copySearchHistoryItem = [...searchHistoryItem];
      setLastResearchState(
        copySearchHistoryItem.slice(0, 6).map(({ term }) => term),
      );
    }
  }, [searchHistoryItem]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const clickEvent = (e: any) => {
      if (!open && e.target && e.target.id && IDS.includes(e.target.id)) {
        setOpen(true);
        triggerEvent("Search Started");
        return;
      }

      if (
        !open &&
        e.target &&
        e.target &&
        e.target.firstChild &&
        IDS.includes(e.target.firstChild.id)
      ) {
        setOpen(true);
        triggerEvent("Search Started");
        return;
      }

      if (!open) setOpen(false);
    };
    document.addEventListener("click", clickEvent);

    return () => {
      document.removeEventListener("click", clickEvent);
    };
  }, []);

  const triggerSearch = useCallback(
    (search: string) => {
      resetAutoCompleteSearchBarState();
      const copyLastResearchState = [...lastResearchState];

      const indexOfTheSameSearch = copyLastResearchState.findIndex(
        (value) => value === search,
      );

      if (indexOfTheSameSearch !== -1)
        copyLastResearchState.splice(indexOfTheSameSearch, 1);

      copyLastResearchState.unshift(search);

      if (copyLastResearchState.length > 6) copyLastResearchState.pop();

      setLastResearchState([...copyLastResearchState]);

      updateTextOfSearchBar(search);
      updateTextOfSearchTextBar(search);

      triggerEvent("Search Object", {
        props: search,
      });

      navigate("/explorer");
    },
    [lastResearchState],
  );

  let restaurantsAutoComplet: string[] = [];

  if (data && data.autocomplete) {
    restaurantsAutoComplet = data.autocomplete
      .filter(
        (value) => value && value.type === AutocompleteResultType.RESTAURANT,
      )
      .map((value) => value?.value) as string[];
  }

  return (
    <Popover open={open}>
      <VisuallyHidden>
        <PopoverTrigger />
      </VisuallyHidden>
      <Input
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
        }}
        onKeyUp={({ code }) => {
          if (code === "Enter") {
            setOpen(false);
            triggerSearch(value);
          }
        }}
        id="search-bar-input"
        className="w-[70%] sm:w-[446px]"
        classNameInput="rounded-[72px] bg-[#F7F8F9] border-none"
        placeholder={t("search_bar_placeholder")}
        endIcon={Search}
        canClear={Boolean(value)}
        onClear={() => {
          triggerSearch("");
          setValue("");
        }}
      />

      <PopoverContent
        id="search-bar-popover"
        className="w-96 sm:w-[780px] max-h-[600px] absolute top-[30px] overflow-y-auto bg-[#FFFFFF] flex flex-col gap-[24px] p-[24px] rounded-[12px]"
      >
        {lastResearchState.length > 0 && (
          <div>
            <Text color="#758195" fontSize={14} fontWeight={400}>
              {t("recent_searches")}
            </Text>
            <div className="grid grid-cols-2 gap-[12px] mt-[16px]">
              {lastResearchState.map((lastResearch, index) => (
                <div
                  key={`last_research_${lastResearch}_${index}`}
                  className="flex items-center p-[8px] gap-[8px] rounded-[8px] hover:bg-[#F1F2F4] cursor-pointer"
                  onClick={() => {
                    triggerSearch(lastResearch);
                    setValue(lastResearch);
                  }}
                >
                  <HistoryIcon height={16} width={16} color="#758195" />
                  <Text fontSize={14} fontWeight={400}>
                    {lastResearch}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        )}

        {suggestions.length > 0 && (
          <div>
            <Text color="#758195" fontSize={14} fontWeight={400}>
              {t("addresses")}
            </Text>
            <div className="flex flex-col gap-[12px] mt-[16px]">
              {suggestions.map((it) => (
                <div
                  key={`famous_place_${it.address}`}
                  className="flex items-center p-[8px] gap-[8px] rounded-[8px] hover:bg-[#F1F2F4] cursor-pointer"
                  onClick={() => {
                    if (it.toPlace) {
                      fetchFieldsDetails(it.toPlace);
                      setValue(it.address);
                      triggerEvent("Search Object", {
                        props: it.address,
                      });
                      updateTextOfSearchTextBar(it.address);
                      setTimeout(() => {
                        updateTextOfSearchBar("");
                      }, 1000);

                      navigate("/explorer");
                    }
                  }}
                >
                  <Text fontSize={14} fontWeight={400}>
                    {it.address}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        )}

        {restaurantsAutoComplet.length > 0 && (
          <div>
            <Text color="#758195" fontSize={14} fontWeight={400}>
              {t("restaurants")}
            </Text>
            <div className="flex flex-col gap-[12px] mt-[16px]">
              {restaurantsAutoComplet.map((famousPlace) => (
                <div
                  key={`famous_place_${famousPlace}`}
                  className="flex items-center p-[8px] gap-[8px] rounded-[8px] hover:bg-[#F1F2F4] cursor-pointer"
                  onClick={() => {
                    setValue(famousPlace);
                    triggerSearch(famousPlace);
                  }}
                >
                  <Text fontSize={14} fontWeight={400}>
                    {famousPlace}
                  </Text>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <Text color="#758195" fontSize={14} fontWeight={400}>
            {t("popular_locations")}
          </Text>
          <div className="grid grid-cols-3 gap-[12px] mt-[16px]">
            {FAMOUS_PLACE[
              navigator.language.split("-")[0] === "fr" ? "fr" : "en"
            ].map((famousPlace) => (
              <div
                key={`famous_place_${famousPlace.name}`}
                className="flex items-center p-[8px] gap-[8px] rounded-[8px] hover:bg-[#F1F2F4] cursor-pointer"
                onClick={() => {
                  setValue(famousPlace.name);
                  triggerSearch("");
                  setGooglePlace({ ...famousPlace.position, zoomUp: false });
                }}
              >
                <MapPinnedIcon height={24} width={24} color="#E2483D" />
                <Text fontSize={14} fontWeight={400}>
                  {famousPlace.name}
                </Text>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SearchBar;
